import intlTelInput from "intl-tel-input/intlTelInputWithUtils";
import { FormGroup, FormControl, Validators } from "../Core/reactive-forms.js";
import { showMessage } from "../Core/ui-utils.js";
import { ApiResponse } from "../Core/api-models.js";
import { initSlider } from "../Core/slider.js";

const registerEndpoint = "/Promotion/RegisterAtun";

document.addEventListener("DOMContentLoaded", () => {
  // Inicializar intl-tel-input para TODOS los campos marcados como 'tel'
  const itiInstances: any = {};
  document.querySelectorAll(".js-iti-phone").forEach((wrapper: any) => {
    const input = wrapper.querySelector("input") as HTMLInputElement;
    if (input) {
      const controlName = input.getAttribute("formControlName");
      if (controlName) {
        itiInstances[controlName] = intlTelInput(input, {
          initialCountry: "pe",
          countryOrder: ["pe"],
          separateDialCode: false,
          i18n: {
            searchPlaceholder: "Buscar país",
          },
        });
      }
    }
  });
  (window as any).itiInstances = itiInstances;

  // 1. Leer Metadata Generada por C# e inicializar FormGroup Automáticamente
  const metadata = (window as any).__FORM_METADATA;

  if (!metadata) {
    console.error("No form metadata found from C#!");
    return;
  }

  const controls: any = {};
  const bindMessages: any = {};

  Object.keys(metadata).forEach((key) => {
    const fieldMeta = metadata[key];
    const validatorsArray: any[] = [];
    const messages: any = {};

    fieldMeta.validators.forEach((v: string) => {
      if (v === "required") {
        validatorsArray.push(Validators.required);
        messages.required = "Este campo es obligatorio.";
      } else if (v === "requiredTrue") {
        validatorsArray.push(Validators.requiredTrue);
        messages.requiredTrue = "Debe aceptar los términos y condiciones.";
      } else if (v === "email") {
        validatorsArray.push(Validators.email);
        messages.email = "Por favor, introduce una dirección de correo válida.";
      } else if (v.startsWith("minLength:")) {
        const len = parseInt(v.split(":")[1], 10);
        validatorsArray.push(Validators.minLength(len));
        messages.minlength = `Debe ingresar al menos ${len} caracteres.`;
      } else if (v.startsWith("pattern:")) {
        const pattern = v.substring("pattern:".length);
        validatorsArray.push(Validators.pattern(new RegExp(pattern)));
        messages.pattern = "Formato inválido.";
      }
    });

    // Validación de Teléfono dinámica para cualquier campo 'tel'
    if (fieldMeta.type === "tel") {
      validatorsArray.push((val: any) => {
        const iti = (window as any).itiInstances?.[key];
        if (iti && val) {
          return iti.isValidNumber() ? null : { invalidPhone: true };
        }
        return null;
      });
      messages.invalidPhone = "El número telefónico no es válido.";
    }

    controls[key] = new FormControl(
      fieldMeta.type === "checkbox" ? false : "",
      validatorsArray,
    );
    bindMessages[key] = messages;
  });

  const registerForm = new FormGroup(controls);

  // 2. Elemento del formulario
  const formElement = document.getElementById(
    "registerForm",
  ) as HTMLFormElement;

  if (formElement) {
    // 3. Vincular automáticamente todo el formulario al DOM de forma 100% reactiva.
    registerForm.bindToDOM(formElement, bindMessages);

    // 4. Botón de reinicio (Reset/Borrar)
    formElement.addEventListener("reset", () => {
      registerForm.reset();
    });

    // 5. Enviar formulario reactivo validado
    formElement.addEventListener("submit", async (e: Event) => {
      e.preventDefault();

      // Validar teléfonos dinámicamente
      let allPhonesValid = true;
      Object.keys((window as any).itiInstances || {}).forEach((key) => {
        const iti = (window as any).itiInstances[key];
        const control = registerForm.get(key);
        if (iti && !iti.isValidNumber()) {
          control?.setErrors({ invalidPhone: true });
          control?.markAsTouched();
          allPhonesValid = false;
        }
      });

      if (!allPhonesValid) return;

      // Validar si el formulario reactivo es válido
      if (registerForm.invalid) {
        Object.keys(registerForm.controls).forEach((key) => {
          registerForm.get(key)?.markAsTouched();
        });
        return;
      }

      const submitBtn = formElement.querySelector(
        'app-button[type="submit"]',
      ) as any;
      const originalBtnText = submitBtn?.getAttribute("text") || "Enviar";

      try {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.setAttribute("text", "SUBIENDO...");
        }

        // 1. Obtener valores básicos del formulario
        const payload = { ...registerForm.value };

        // Función auxiliar para subir un File al servidor
        const uploadFile = async (file: File): Promise<string> => {
          const formData = new FormData();
          formData.append("file", file);
          const response = await fetch("/Promotion/PostLoadFileV2", {
            method: "POST",
            body: formData,
          });
          const result: ApiResponse<string> = await response.json();
          if (result.success && result.data) return result.data;
          throw new Error(result.message || "Error al subir imagen");
        };

        // 2. Identificar y subir TODOS los archivos de forma dinámica
        const fileFields = Object.keys(metadata).filter(
          (key) => metadata[key].type === "file",
        );
        const uploadPromises = fileFields.map(async (key) => {
          const fileOrFiles = payload[key];
          if (fileOrFiles instanceof File) {
            // Un solo archivo
            const url = await uploadFile(fileOrFiles);
            payload[key] = url;
            return { key, url };
          } else if (
            fileOrFiles instanceof FileList &&
            fileOrFiles.length > 0
          ) {
            // Múltiples archivos: subir cada uno y guardar array de URLs
            const urls: string[] = [];
            for (let i = 0; i < fileOrFiles.length; i++) {
              const url = await uploadFile(fileOrFiles[i]);
              urls.push(url);
            }
            payload[key] = urls; // string[] de URLs
            return { key, url: urls };
          }
          return { key, url: null };
        });

        await Promise.all(uploadPromises);

        // 3. Obtener los números de teléfono internacionales
        Object.keys((window as any).itiInstances || {}).forEach((key) => {
          const iti = (window as any).itiInstances[key];
          if (iti) {
            payload[key] = iti.getNumber().replace(/^\+/, "");
          }
        });

        console.log("¡Archivos subidos y datos listos!", payload);

        // (Opcional) Restaurar botón si luego quieres redirigir
        //submitBtn.innerText = '¡LISTO!';

        // Aquí podrías hacer el fetch final al endpoint de registro con el 'payload' completado
        const finalResponse = await fetch(registerEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (finalResponse.ok) {
          const result: ApiResponse = await finalResponse.json();
          if (result.success) {
            const redirectUrl = (result.data as any)?.redirectUrl;
            window.location.href = redirectUrl ?? "/Promotion/Success";
          }
        } else {
          const errorData: ApiResponse = await finalResponse.json();
          console.error("Error del servidor:", errorData);
          throw new Error(
            errorData.message ||
              "No se pudo completar el registro. Verifica los datos.",
          );
        }
      } catch (err: any) {
        console.error("Error durante el registro:", err);
        showMessage(
          "Error",
          err.message || "Ocurrió un error al procesar el formulario.",
          "error",
          {
            extraButtons: [
              {
                text: "Ir a ayuda",
                variant: "dialog",
                onRedirect: "/Promotion/Help",
              },
            ],
          },
        );
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.setAttribute("text", originalBtnText);
        }
      }
    });
  }

  // Inicializar carruseles de imágenes referenciales
  initSlider({
    modalId: "img-referencial",
    viewportId: "slider-viewport",
    trackId: "slider-track",
    btnLeftId: "btn-slide-left",
    btnRightId: "btn-slide-right",
    visibleLg: 3,
    visibleMd: 2,
    visibleSm: 1,
    disableOnMobile: true,
  });

  initSlider({
    modalId: "foto-referencial",
    viewportId: "foto-slider-viewport",
    trackId: "foto-slider-track",
    btnLeftId: "btn-foto-slide-left",
    btnRightId: "btn-foto-slide-right",
    visibleLg: 1,
    visibleMd: 1,
    visibleSm: 1,
  });
});
